import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { assetsApi, CurrentAssignment, assetCategoriesApi, assetLocationsApi, AssignableUser } from '../../api';
import { AssetCategory, AssetLocation } from '../../types';
import { Card } from '../../components/common/Card';
import { Input } from '../../components/common/Input';
import { Icons } from '../../components/common/Icons';
import { usePermissions } from '../../hooks/usePermissions';
import './AssetAssignments.css';

const PER_PAGE = 50;

export function AssetAssignments() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [assignments, setAssignments] = useState<CurrentAssignment[]>([]);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [locations, setLocations] = useState<AssetLocation[]>([]);
  const [employees, setEmployees] = useState<AssignableUser[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [selectedLocation, setSelectedLocation] = useState<number | ''>('');
  const [selectedEmployee, setSelectedEmployee] = useState<number | ''>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [assignmentsRes, categoriesRes, locationsRes] = await Promise.all([
        assetsApi.getCurrentAssignments({
          category_id: selectedCategory || undefined,
          location_id: selectedLocation || undefined,
          employee_id: selectedEmployee || undefined,
          search: searchTerm || undefined,
        }),
        assetCategoriesApi.getCategories(),
        assetLocationsApi.getLocations(),
      ]);

      setAssignments(assignmentsRes.assignments);
      setCategories(categoriesRes.categories);
      setLocations(locationsRes.locations);

      // Fetch assignable employees for filter (excludes Super Admin)
      try {
        const usersRes = await assetsApi.getAssignableUsers();
        setEmployees(usersRes.users);
      } catch {
        // Ignore if can't fetch users
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedLocation, selectedEmployee, searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(assignments.length / PER_PAGE);
  const paginatedAssignments = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return assignments.slice(start, start + PER_PAGE);
  }, [assignments, currentPage]);
  const startItem = assignments.length === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const endItem = Math.min(currentPage * PER_PAGE, assignments.length);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const handleReturnAsset = async (assetDbId: number, assetId: string) => {
    if (!window.confirm(`Are you sure you want to process return for asset ${assetId}?`)) {
      return;
    }

    try {
      await assetsApi.returnAsset(assetDbId, {});
      fetchData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to process return');
    }
  };

  if (!hasPermission('assets.view')) {
    return (
      <div className="permission-denied">
        <Icons.Shield />
        <h2>Access Denied</h2>
        <p>You don't have permission to view asset assignments.</p>
      </div>
    );
  }

  return (
    <div className="asset-assignments-page">
      <div className="page-header">
        <div className="page-title">
          <h1>Asset Assignments</h1>
          <p>View all currently assigned assets and their assignees</p>
        </div>
      </div>

      <Card className="filters-card">
        <div className="asset-filters-panel">
          <div className="filter-search-row">
            <Input
              type="text"
              placeholder="Search by Asset ID, Employee name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-dropdowns-row">
            <select
              className="filter-select"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            <select
              className="filter-select"
              value={selectedLocation}
              onChange={e => setSelectedLocation(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>

            <select
              className="filter-select"
              value={selectedEmployee}
              onChange={e => setSelectedEmployee(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="loading-state">Loading assignments...</div>
        ) : error ? (
          <div className="error-state">
            <Icons.AlertCircle />
            <p>{error}</p>
            <button onClick={fetchData}>Retry</button>
          </div>
        ) : paginatedAssignments.length === 0 ? (
          <div className="empty-state">
            <Icons.Users />
            <h3>No assignments found</h3>
            <p>There are no assets currently assigned to employees</p>
          </div>
        ) : (
          <div className="assignments-table-wrapper">
            <table className="assignments-table">
              <thead>
                <tr>
                  <th>Asset ID</th>
                  <th>Category</th>
                  <th>Model</th>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Assigned On</th>
                  <th>Assigned By</th>
                  <th>Location</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAssignments.map(assignment => (
                  <tr key={assignment.id}>
                    <td>
                      <button 
                        className="link-button"
                        onClick={() => navigate(`/assets`)}
                      >
                        {assignment.asset_id}
                      </button>
                    </td>
                    <td>{assignment.category || '-'}</td>
                    <td>{assignment.model || '-'}</td>
                    <td>
                      <div className="employee-info">
                        <span className="employee-name">{assignment.employee_name}</span>
                        <span className="employee-email">{assignment.employee_email}</span>
                      </div>
                    </td>
                    <td>{assignment.employee_department || '-'}</td>
                    <td>{formatDate(assignment.assigned_at)}</td>
                    <td>{assignment.assigned_by || '-'}</td>
                    <td>{assignment.location || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        {hasPermission('assets.return') && (
                          <button
                            className="icon-button return-btn"
                            title="Process Return"
                            onClick={() => handleReturnAsset(assignment.asset_db_id, assignment.asset_id)}
                          >
                            <Icons.UserMinus />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {assignments.length > 0 ? startItem : 0} to {endItem} of {assignments.length} assignments
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              <Icons.ChevronsLeft />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <Icons.ChevronLeft />
            </button>

            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn page-num ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <Icons.ChevronRight />
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              <Icons.ChevronsRight />
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AssetAssignments;
